// World state — plain data, mutable in place by the physics step,
// serializable to JSON. The world is the input to the physics step
// each tick and the input to the render each frame.
//
// Layout:
//   track          — schema-shaped, immutable across a session
//   geometry       — densified caches built once per track-load
//   cars           — array of cars; each carries its own pose, vel,
//                    derived telemetry, lapState, controllerType.
//                    Per-car state lives on the car object (CLAUDE.md
//                    convention) so the world can shrink/grow grid
//                    size without singleton plumbing.
//   selectedCarIdx — which car the camera + HUD follow. Independent
//                    of which car is being manually driven (a player
//                    can spectate an AI car).
//   camera         — follow-camera position. Singleton because there's
//                    only one viewport.
//   simTime        — accumulated physics time (seconds)

import { buildDensifiedTrack } from '../../shared/track-geometry.js';
import {
  makeNearestResult,
  nearestPointOnDenseInto,
  nearestPointOnDenseWindowedInto,
  closestPointOnSegmentInto,
} from '../../shared/geometry.js';
import { CAR_SPEC_DEFAULTS } from '../../shared/car-schema.js';
import { createCondition } from './condition.js';
import { createRaceState } from './race-state.js';

// Default driver profile. Intentionally minimal — fields here are
// the seam for personality / experience modeling. AI controllers
// READ these but treat them as scalars on existing behavior; mistakes
// (off-tracks, spinouts, missed brake points) emerge from physics
// when the driver asks for too much, not from scripted "make a
// mistake" rolls.
//
//   aggression — 0 = cautious cruise, 1 = ragged-edge late-braker.
//                Today scales: cornering-safety factor, following gap.
//                Tomorrow: dive-bomb decisions, defense behavior.
//   skill      — 0 = inconsistent novice, 1 = robotic veteran.
//                Reserved. Future: variance on brake points + line
//                under pressure (dice rolls against world.rng).
//
// Treat fields you haven't wired yet as forward-compat: add the field,
// document what 0 / 1 mean, leave the AI controller using a safe
// default until the behavior lands.
export const DEFAULT_DRIVER_PROFILE = {
  aggression: 0.5,
  skill:      0.6,
};

// Distinct car liveries — body + accent (front section) per slot.
// 20 entries cover any realistic grid size; cars are auto-assigned
// `LIVERIES[i % LIVERIES.length]` at createWorld so a fresh race
// gets visually unique cars without authoring per-track palettes.
// When per-team / per-class work lands, this becomes the fallback
// for cars without an explicit team livery.
export const LIVERIES = [
  { body: '#e54b3c', accent: '#bf3a2c' },  // red
  { body: '#3a7ad6', accent: '#2c5da5' },  // blue
  { body: '#f5c441', accent: '#c9a235' },  // yellow
  { body: '#4ca85a', accent: '#3a8444' },  // green
  { body: '#e8842c', accent: '#b56923' },  // orange
  { body: '#9b59b6', accent: '#7d4392' },  // purple
  { body: '#1abc9c', accent: '#13927a' },  // teal
  { body: '#ec4899', accent: '#b8336e' },  // pink
  { body: '#94a3b8', accent: '#64748b' },  // silver
  { body: '#0ea5e9', accent: '#075985' },  // sky
  { body: '#10b981', accent: '#047857' },  // emerald
  { body: '#fb923c', accent: '#c2410c' },  // tangerine
  { body: '#a3e635', accent: '#65a30d' },  // lime
  { body: '#f43f5e', accent: '#be123c' },  // rose
  { body: '#8b5cf6', accent: '#6d28d9' },  // violet
  { body: '#d4d4d4', accent: '#737373' },  // grey
  { body: '#1e3a8c', accent: '#0d2266' },  // navy
  { body: '#facc15', accent: '#a16207' },  // amber
  { body: '#a16207', accent: '#713f12' },  // bronze
  { body: '#f87171', accent: '#dc2626' },  // crimson
];

// Mulberry32 — small, fast, deterministic 32-bit PRNG. Seed lives on
// the world; every call advances the same generator. Same seed +
// same sequence of consumers = same dice rolls, which is the
// foundation for replays + debugging the personality layer when it
// lands. Don't reach for Math.random() in any sim path; come here.
export function createSeededRNG(seed = 1) {
  let s = (seed >>> 0) || 1;
  return function next() {
    s = (s + 0x6D2B79F5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Spec defaults live in shared/car-schema.js — single source of truth
// for both the on-disk car format and the runtime defaults. Re-exported
// here under the historical DEFAULT_CAR_SPEC name so existing imports
// inside simulator/ keep working unchanged.
export const DEFAULT_CAR_SPEC = CAR_SPEC_DEFAULTS;

// Per-car state. Pose + velocity are mutated by physicsStep each tick;
// derived telemetry + lapState are mutated by surfaceAt + lap timing.
// `controllerType` is the seam for "manual vs AI" — the physics tick
// reads it each frame and routes to the right controller. Only one car
// can be 'manual' at a time (single keyboard); world-level helpers
// enforce that.
//
// `spec` is shallow-cloned per car so live tuning of one car doesn't
// affect another. When per-car-class specs land, cars sharing a class
// can keep references via a separate path; for now, every car owns
// its values. Same goes for `driverProfile`.
export function createCar(spec = DEFAULT_CAR_SPEC, {
  id = 'car',
  controllerType = 'ai',
  driverProfile = DEFAULT_DRIVER_PROFILE,
  livery = LIVERIES[0],
} = {}) {
  const specCopy = { ...spec };
  return {
    id,
    pos:           { x: 0, y: 0 },
    heading:       0,
    vel:           { x: 0, y: 0 },
    angVel:        0,
    steer:         0,
    spec:          specCopy,
    // effectiveSpec — physics reads from this, NOT spec. Resolved
    // each tick from spec + condition (tire wear, fuel mass, damage)
    // by updateCondition. Pre-populated as a copy of spec so cars
    // can drive on tick 0 before any condition update has run.
    effectiveSpec: { ...specCopy },
    // Condition — tire life, fuel, damage. updateCondition mutates
    // this each tick; applyImpactDamage mutates damage on collisions.
    condition:     createCondition(specCopy),
    driverProfile: { ...driverProfile },
    livery:        { ...livery },
    controllerType,                                     // 'manual' | 'ai'
    // Drive mode — pace dial set by team-control. 'normal' is the
    // baseline; 'push' tightens cornering / sharpens throttle for
    // faster laps at the cost of more wear + more fuel; 'conserve'
    // does the opposite (lift early, accelerate late, lower corner
    // speeds) to stretch tires and fuel. Read by ai-controller.js
    // via DRIVE_MODES — adding a new mode means an entry there.
    driveMode:  'normal',
    // Pit state — set by team-control commands. The AI controller and
    // the main tick's phase machine read this to switch line sources:
    //   phase === 'racing'  → AI follows racing line (default)
    //   phase === 'pitting' → AI follows pit route (entry → pit-lane
    //                         spline → exit), capped at pit speed limit
    //   requested === true  → racing-phase car will commit to pitting
    //                         when its racing-line arc nears the pit
    //                         entry. Cleared on commit.
    pitState:   {
      phase: 'racing', requested: false, serviceEndsAt: null, serviced: false,
      // Pit order — what services to perform at the next stop. The
      // boolean flags gate each service on/off; the numeric amounts
      // (fuelKg, repairFrac) let the strategist dial how much to do
      // when the service is enabled. Both amount fields are nullable
      // — null means "do the maximum sensible thing" (fill tank to
      // cap; repair all current damage). The UI can leave them null
      // until the player drags a slider, at which point they become
      // explicit numbers. Tires is all-or-nothing (one stint of
      // rubber, no partials).
      // Defaults: tires + full refuel (the common case); damage
      // repair opt-in because it's a long add (30 s for a totaled
      // car) the player should pick deliberately.
      // See computePitServiceSeconds for the time math and
      // applyPitOrder for what actually changes on completion.
      order: { tires: true, fuel: true, repair: false, fuelKg: null, repairFrac: null },
    },
    // Stops completed this race — incremented when a box service window
    // closes (see applyPitOrder in main.js). Surfaces in the telemetry
    // HUD and final-results table.
    pitStops:   0,
    // Index into geometry.pitBoxes.boxes — which stall this car pulls
    // into on a pit stop. Assigned once at world creation (1:1 by car
    // index for now; team-grouped assignment when teams matter). null
    // if there are more cars than boxes — those cars fall back to
    // stopping at the speed-zone midpoint.
    pitBoxIdx:  null,
    derived:    createDerived(),
    lapState:   createLapState(),
    lastInputs: { throttle: 0, brake: 0, steerTarget: 0 },
  };
}

export function createDerived() {
  return {
    fwdSpeed:  0,
    latSpeed:  0,
    lateralG:  0,
    longG:     0,
    slipAngle: 0,
    slipF:     0,
    slipR:     0,
    steerAuth: 1,
    surface:   'tarmac',
    // Per-car racing-line cache. Updated once per tick in
    // updateLineCache (called from main.js BEFORE controllers run).
    // AI controller + traffic helpers read these instead of recomputing
    // a full nearestPointOnDense per call — kills the O(N²) traffic
    // scan and lets the AI's own line lookup be a tiny windowed search
    // around the previous tick's anchor.
    //   lineSegIdx === -1 → cache cold; next updateLineCache does a
    //                        full scan to seed it.
    //   lineArc            → arc length at the nearest line point, in
    //                        meters from the line's seam (matches
    //                        arcLengthAtNp output).
    //   linePointX/Y       → coordinates of the nearest line point.
    lineSegIdx: -1,
    lineArc:    0,
    linePointX: 0,
    linePointY: 0,
    // Racing-line projection — always against world.geometry.racingLine
    // regardless of pit phase. Used by standings + announcer / any
    // consumer that needs a canonical "where is this car around the
    // lap?" arc independent of which spline the AI is following. While
    // the car is racing this is a free copy of lineArc/lineSegIdx;
    // while pitting it's an explicit second projection.
    racingLineSegIdx: -1,
    racingLineArc:    0,
    // Track-context location, derived from the car's projected
    // centerline arc + track.labels. Updated on a coarse cadence by
    // the main tick loop (every ~10 ticks → ~6 Hz) since HUD /
    // announcer captions don't need physics rates. Values are the
    // text strings authored in the editor — UI surfaces them as-is.
    //   currentCorner: the nearest corner label within
    //                  CORNER_PROXIMITY_M of the car's centerline
    //                  arc, else null.
    //   currentArea:   the area label whose [startArc, endArc] range
    //                  (forward around the lap) contains the car,
    //                  else null. Areas can overlap; this resolves
    //                  to the first match in authoring order.
    currentCorner: null,
    currentArea:   null,
    // Tick countdown for the coarse-cadence currentLocation refresh.
    _locationTicksLeft: 0,
  };
}

// Scratch results for line-cache updates. Reused across all cars;
// safe because the loop is sequential and each consumer copies out
// the fields it needs before the next call clobbers the result.
// Two separate scratches because updateLineCache and
// updateRacingLineCache can run back-to-back per car (when pitting)
// and one of them shouldn't clobber the other mid-call.
const _lineCacheScratch       = makeNearestResult();
const _racingLineCacheScratch = makeNearestResult();
const _anchorScratch          = { dist: 0, t: 0, point: { x: 0, y: 0 } };
const ARC_CACHE_WINDOW  = 12;   // segments — covers ~25 m at 14 subs/wp

// Refresh a car's nearest-point cache against whichever line is
// currently active for it (racing line OR pit route). Cheap when warm
// (windowed search of ±ARC_CACHE_WINDOW segments around last anchor);
// falls back to a full scan when cold or when the windowed search hits
// its edge (a sign the car teleported, spun, or just transitioned
// between line sources). The line argument carries `closed` — racing
// line wraps; pit route doesn't.
export function updateLineCache(car, line) {
  if (!line || !line.dense || line.dense.length < 4) {
    car.derived.lineSegIdx = -1;
    return;
  }
  const closed = line.closed !== false;
  const np = car.derived.lineSegIdx >= 0
    ? nearestPointOnDenseWindowedInto(
        car.pos, line.dense, closed,
        car.derived.lineSegIdx, ARC_CACHE_WINDOW, _lineCacheScratch
      )
    : nearestPointOnDenseInto(
        car.pos, line.dense, closed, _lineCacheScratch
      );
  if (!np) {
    car.derived.lineSegIdx = -1;
    return;
  }
  car.derived.lineSegIdx = np.segmentIdx;
  car.derived.lineArc    = line.cumulative[np.segmentIdx]
                         + np.t * line.segLengths[np.segmentIdx];
  car.derived.linePointX = np.point.x;
  car.derived.linePointY = np.point.y;
}

// True when the car is on the pit route — driving down it ('pitting')
// or stopped in its box ('boxStopped'). Both phases share the same
// active line, line cache, and edge-proximity reference frame; only
// the AI's outputs differ. Single helper so the half-dozen "is this
// car on pit?" checks across the sim stay consistent.
export function isOnPitRoute(car) {
  const p = car?.pitState?.phase;
  return p === 'pitting' || p === 'boxStopped';
}

// Pick the line source a car is currently following. Defaults to the
// racing line. When pit phase is active AND a pit route is authored,
// returns the pit route. Falls back to racing line if pit phase is
// active but the track has no pit route (defensive — shouldn't happen
// since requestPit is gated on pit-route presence).
export function activeLineFor(car, geometry) {
  if (isOnPitRoute(car) && geometry.pitRoute) {
    return geometry.pitRoute;
  }
  return geometry.racingLine;
}

// Refresh the racing-line projection. When the car is racing, the
// active line IS the racing line, so this is a free mirror of the
// AI's lineArc/lineSegIdx. When pitting, the AI follows the pit
// route while standings still need a "where on the racing line is
// this car?" answer — separate windowed projection back onto the
// racing line. Cheap: warm cache is ±ARC_CACHE_WINDOW (~25 m) of
// segment math; cold cache (cars-on-grid) is one full scan.
export function updateRacingLineCache(car, geometry) {
  const rl = geometry.racingLine;
  if (!rl || !rl.dense || rl.dense.length < 4) {
    car.derived.racingLineSegIdx = -1;
    return;
  }
  if (!isOnPitRoute(car)) {
    car.derived.racingLineSegIdx = car.derived.lineSegIdx;
    car.derived.racingLineArc    = car.derived.lineArc;
    return;
  }
  const np = car.derived.racingLineSegIdx >= 0
    ? nearestPointOnDenseWindowedInto(
        car.pos, rl.dense, true,
        car.derived.racingLineSegIdx, ARC_CACHE_WINDOW, _racingLineCacheScratch
      )
    : nearestPointOnDenseInto(car.pos, rl.dense, true, _racingLineCacheScratch);
  if (!np) {
    car.derived.racingLineSegIdx = -1;
    return;
  }
  car.derived.racingLineSegIdx = np.segmentIdx;
  car.derived.racingLineArc    = rl.cumulative[np.segmentIdx]
                               + np.t * rl.segLengths[np.segmentIdx];
}

// Anchor the line cache at the FIRST locally-nearest segment of an
// open route, walking forward from segment 0. Required when
// transitioning onto an open polyline that runs near the racing line
// elsewhere — a global nearest-segment scan can return a mid-route
// segment that is geometrically closer than the entry, but doesn't
// reflect the car's actual progress along the route.
//
// Real-world failure: pit lanes that run parallel to the start /
// finish straight sit ~10 m laterally from the racing line. A car
// committing to pit just before the entry marker can be physically
// closer to a mid-pit-lane segment than to the entry-ribbon start;
// full-scan caching anchors the AI mid-route, pure-pursuit then
// steers across grass into the pit wall. Especially common for
// trailing cars in a multi-car pit train, where car-vs-car collisions
// nudge them sideways before the transition tick.
//
// "First local minimum" walks forward from segment 0 keeping the
// running-best distance, and bails a few segments after distance
// starts rising again. The few-segment hysteresis keeps us from
// stopping at a noise bump on the very first segments.
export function anchorLineCacheAtRouteStart(car, route) {
  const dense = route.dense;
  const N     = dense.length;
  if (N < 2) {
    car.derived.lineSegIdx = -1;
    return;
  }
  let bestSegIdx = 0;
  let bestT      = 0;
  let bestDist   = Infinity;
  let bestPx = 0, bestPy = 0;
  let increasing = 0;
  for (let i = 0; i < N - 1; i++) {
    closestPointOnSegmentInto(car.pos, dense[i], dense[i + 1], _anchorScratch);
    if (_anchorScratch.dist < bestDist) {
      bestDist  = _anchorScratch.dist;
      bestSegIdx = i;
      bestT     = _anchorScratch.t;
      bestPx    = _anchorScratch.point.x;
      bestPy    = _anchorScratch.point.y;
      increasing = 0;
    } else if (++increasing > 5) {
      break;
    }
  }
  car.derived.lineSegIdx = bestSegIdx;
  car.derived.lineArc    = route.cumulative[bestSegIdx]
                         + bestT * route.segLengths[bestSegIdx];
  car.derived.linePointX = bestPx;
  car.derived.linePointY = bestPy;
}

export function createCamera() {
  return { x: 0, y: 0 };
}

export function createLapState() {
  return {
    prevPos:            null,
    // lapNumber === 0 is the out lap before the first S/F crossing —
    // the timer doesn't run until the car actually crosses the line.
    lapNumber:          0,
    currentLapStart:    null,
    lastLapTime:        null,
    bestLapTime:        null,
    // currentSector doubles as a validation counter:
    //   0 = in S1 (need to cross sector0 gate)
    //   1 = in S2 (need sector1 gate)
    //   2 = in S3 (next S/F completes the lap)
    currentSector:      0,
    currentSectorStart: null,
    // currentLapSectorTimes — sectors completed during the in-progress
    //   lap. Reset at every lap start, filled as gates are crossed.
    // lastSectorTimes — frozen snapshot of the most recent completed
    //   lap; acts as the per-cell fallback so the panel shows prior-
    //   lap sectors until the new lap overwrites them one-by-one.
    currentLapSectorTimes: [null, null, null],
    lastSectorTimes:       [null, null, null],
    bestSectorTimes:       [null, null, null],
    // True while the in-progress lap was started at a separate
    // startLine (a "standing start" lap on tracks where grid is
    // offset from S/F). Such laps complete normally and record a
    // lastLapTime, but are excluded from bestLapTime / best sector
    // times — the lap is physically shorter, so it isn't a fair
    // comparator for "fastest lap" awards. Matches IndyCar/F1
    // timing convention. Resets to false on lap completion.
    currentLapStandingStart: false,
  };
}

// Build a fresh world for a loaded track. The track must already have
// been schema-validated via shared/track-schema's loadTrack.
//
// Two grid-construction modes:
//   - `entries` (preferred): explicit per-car array carrying { spec,
//     livery, driverProfile, displayName, carNumber, team } for a
//     team-based grid (the roster picker emits this). Each entry
//     becomes one car with that team's chassis + driver profile.
//   - `gridSize` (legacy / quick-start): clones one spec N times with
//     auto-assigned LIVERIES. Used by direct-link race URLs and the
//     editor's "Test track" flow.
//
// Every car starts in 'ai' mode — the player clicks a car (camera
// follows) and presses 'M' to take over.
export function createWorld(track, opts = {}) {
  const {
    entries  = null,
    spec     = DEFAULT_CAR_SPEC,
    gridSize = 2,
    seed     = 1,
  } = opts;

  const cars = [];
  if (Array.isArray(entries) && entries.length > 0) {
    for (let i = 0; i < entries.length; i++) {
      const e = entries[i];
      const car = createCar(e.spec ?? spec, {
        id:             `car${i}`,
        controllerType: 'ai',
        livery:         e.livery ?? LIVERIES[i % LIVERIES.length],
        driverProfile:  e.driverProfile ?? DEFAULT_DRIVER_PROFILE,
      });
      // Display metadata — read by the HUD / standings / announcer.
      // Not consumed by physics or controllers, so stays optional.
      // displayName is the compact form for tight UIs (T&S list);
      // fullName is the prose form for the telemetry header.
      if (e.displayName) car.displayName = e.displayName;
      if (e.fullName) car.fullName = e.fullName;
      if (typeof e.carNumber === 'number') car.carNumber = e.carNumber;
      if (e.team) car.team = { id: e.team.id, name: e.team.name, shortName: e.team.shortName };
      // Driver ID round-trips through to the car so the race page can
      // resolve a setup-page driver pick (solo mode "I own this guy")
      // back to a car index after the world is built.
      if (e.driverId) car.driverId = e.driverId;
      // Class identity from the source series (multi-class hook). Not
      // consumed yet — future T&S / results will surface per-class
      // standings without needing a second resolve pass. Single-class
      // series give every car the same classId, which is harmless.
      if (e.classId)   car.classId   = e.classId;
      if (e.className) car.className = e.className;
      cars.push(car);
    }
  } else {
    for (let i = 0; i < gridSize; i++) {
      cars.push(createCar(spec, {
        id:             `car${i}`,
        controllerType: 'ai',
        livery:         LIVERIES[i % LIVERIES.length],
      }));
    }
  }

  const geometry = buildDensifiedTrack(track);

  // Pit-box assignment — spread cars evenly across the available
  // boxes so a 10-car grid on a 24-box lane uses slots 0/2/4/.../21
  // (centered, balanced) instead of clustering at slots 0..9. Looks
  // more like a real pit lane and leaves natural buffer room between
  // teams. When team identity becomes a first-class scheduling
  // concept, group teammates onto the same box (real F1 = one box
  // per team, two cars share). Cars beyond box capacity get null
  // and fall back to stopping at the speed-zone midpoint.
  const boxCount = geometry.pitBoxes?.maxCount ?? 0;
  const N = cars.length;
  for (let i = 0; i < N; i++) {
    if (boxCount === 0) {
      cars[i].pitBoxIdx = null;
    } else if (N <= boxCount) {
      cars[i].pitBoxIdx = Math.floor(i * boxCount / N);
    } else {
      cars[i].pitBoxIdx = i < boxCount ? i : null;
    }
  }

  return {
    track,
    geometry,
    cars,
    selectedCarIdx:  0,
    camera:          createCamera(),
    simTime:         0,
    rng:             createSeededRNG(seed),
    raceState:       createRaceState(),
  };
}

// Select a car. Selecting a DIFFERENT car deactivates manual drive
// on the previously-selected one — the user gave up the wheel by
// looking elsewhere. The newly-selected car stays AI; the user has
// to press 'M' to take it over (per the design discussion: selection
// and drive ownership are separate actions).
//
// Selecting the same car is a no-op so a stray click on the current
// driver doesn't kick them out of manual.
export function selectCar(world, idx) {
  if (idx < 0 || idx >= world.cars.length) return;
  if (idx === world.selectedCarIdx) return;
  for (const other of world.cars) {
    if (other.controllerType === 'manual') other.controllerType = 'ai';
  }
  world.selectedCarIdx = idx;
}

// Toggle the selected car between manual and AI. Enforces the
// "at most one manual" invariant — if the selected car becomes
// 'manual', any other 'manual' car is demoted to 'ai'. This is
// what the 'M' key calls.
//
// Solo-mode gate: the player can only take over their locked driver.
// Pressing M while spectating someone else is a no-op — the team
// owns the wheel of that car, not the player.
export function toggleSelectedDriveMode(world) {
  const car = world.cars[world.selectedCarIdx];
  if (!car) return;
  const rs = world.raceState;
  if (rs?.raceMode === 'solo' && world.selectedCarIdx !== rs.soloCarIdx) return;
  if (car.controllerType === 'manual') {
    car.controllerType = 'ai';
    return;
  }
  for (const other of world.cars) {
    if (other !== car && other.controllerType === 'manual') {
      other.controllerType = 'ai';
    }
  }
  car.controllerType = 'manual';
}
